import React, {useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Modal from '../common/Modal';
import {SelectFieldHookForm} from '../ui/SelectField';
import {skillType, skillTypeOptions, validations} from '../../utils/constants';
import InputField from '../ui/InputField';
import Button from '../ui/Button';
import {useAddSkillMutation, useUpdateSkillMutation} from '../../api/candidate';
import toast from 'react-hot-toast';
import {invalidateQueryV2} from '../../services/reactQuery/index';

const skillSchema = yup.object().shape({
  skillName: yup
    .string()
    .required('Skill Name is required')
    .matches(validations.notAllowedSpace, 'Skill Name is required'),
  skillType: yup.object().nullable().required('Skill type is required'),
});

const AddSkillModal = ({skillDetails = null, editMode = false, ...props}) => {
  const {mutate: addSubjectMutation, isLoading: isAddSubjectLoading} =
    useAddSkillMutation();
  const {mutate: updateSubjectMutation, isLoading: isUpdateSubjectLoading} =
    useUpdateSkillMutation();

  const [showError, toggleShowError] = useState(false);

  const defaultFormValues = useMemo(() => {
    if (editMode) {
      const {type, name} = skillDetails;
      return {
        skillType: {value: type, label: skillType[type]},
        skillName: name,
      };
    }
    return {
      skillType: '',
    };
  }, [editMode, skillDetails]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: {errors, isValid},
    getValues,
  } = useForm({
    resolver: yupResolver(skillSchema),
    mode: 'all',
    defaultValues: defaultFormValues,
  });

  const [skillsSet, setSkillSetValues] = useState([]);

  const onSubjectSuccess = () => {
    toast.success(`Skill ${editMode ? 'updated' : 'added'} Successfully`);
    reset();
    props.onClose();
    props.refetch();
  };

  const mutationOptions = {
    onSuccess: onSubjectSuccess,
    onError: (data) => toast.error(data.response.data.message),
  };

  // For Edit
  const handleFormSubmission = (values) => {
    const {
      skillType: {value: skillType},
      skillName,
    } = values;

    const skillObj = {
      name: skillName,
      type: +skillType,
    };

    if (editMode) {
      const payload = {
        ...skillObj,
        skillId: skillDetails._id,
      };
      updateSubjectMutation(payload, mutationOptions);
    }
  };

  const addNewSkil = (values = undefined) => {
    // submit form to add skills
    if (!values && skillsSet?.length > 0) {
      addSubjectMutation({skills: skillsSet}, mutationOptions);
      // when no skills added to the array
    } else if (!values && skillsSet?.length <= 0) {
      toggleShowError(true);
    } else {
      // call on click of add more button to add skill in the array
      if (isValid) {
        const val = getValues();
        if (val.skillName !== '' && skillType !== undefined) {
          if (val.skillName) {
            const {
              skillName: name,
              skillType: {value: type},
            } = val;
            skillsSet.push({name, type});
            setSkillSetValues([...skillsSet]);
            reset();
          }
        }
      }
    }
  };

  return (
    <Modal {...props} containerClassName="max-w-[600px]">
      <form
        className="mt-3 px-2"
        onSubmit={handleSubmit(editMode ? handleFormSubmission : addNewSkil)}
      >
        <div className="bg-white flex flex-col gap-6">
          <div className="flex flex-col">
            <label className="font-semibold mb-2.5">Skill Name</label>
            <InputField
              {...register('skillName')}
              name="skillName"
              type="text"
              placeholder="enter skill"
              error={errors.skillName?.message}
            />
          </div>

          <div className="flex flex-col">
            <label className="font-semibold mb-2.5">Type</label>
            <SelectFieldHookForm
              placeholder="select skill type"
              control={control}
              className="w-full"
              name="skillType"
              options={skillTypeOptions}
              error={errors.skillType?.message}
            />
          </div>
          {!editMode && (
            <>
              <div>
                <Button type="submit" variant="outlined">
                  Add More
                </Button>
              </div>
              <div className="flex ">
                {skillsSet.map((item, i) => (
                  <div
                    className={`${
                      item.type === '1' ? 'bg-slate-500' : 'bg-blue-400'
                    } mr-3 text-white p-3 inline-block max-w-fit`}
                    key={item.name + i}
                  >
                    {item.name}
                    <div
                      className="ml-3 rounded-full text-slate-800 cursor-pointer inline-block  "
                      onClick={() => {
                        skillsSet.splice(i, 1);
                        setSkillSetValues([...skillsSet]);
                      }}
                    >
                      x
                    </div>
                  </div>
                ))}
              </div>
              {!(skillsSet?.length > 0) && showError && (
                <div className="text-red-600 text-sm w-full text-center">
                  Please add at least one skill
                </div>
              )}
            </>
          )}

          <footer className="text-center">
            <Button
              loading={isAddSubjectLoading || isUpdateSubjectLoading}
              type={editMode ? 'submit' : 'button'}
              onClick={() => (editMode ? null : addNewSkil(undefined))}
            >
              Submit
            </Button>
          </footer>
        </div>
      </form>
    </Modal>
  );
};

export default AddSkillModal;
